import { makeAutoObservable } from "mobx"
import React from "react"

import { avy_api_pkg_common_TenantContract, TenantAdminService } from "src/api"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { parseDate } from "src/lib/date"

import { autoDownloadFile } from "src/lib/file"
import { CustomTenantAdminService } from "src/api/_custom/services/TenantAdminService"
import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"
import { MixpanelProperties } from "src/analytics/constants/properties"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { IExtendedLeaseContractRequest } from "src/views/contracts/pages/list/types/contract-list"
import { loads } from "src/channel/utils"

//#region Context and Loading Keys
export class ContractsListStore {
    static Context = React.createContext<ContractsListStore | null>(null)
    static LoadingKeys = createLoadingKeys("pageLoad", "export", "init")
    //#endregion

    //#region State
    accessGroupId = DEFAULT_ACCESS_GROUP.id
    segments: number[] = []
    statusFilter: string[] = []
    dateRangeFilter: [Date | null, Date | null] = [null, null]
    adminId = -1

    private repositoryUpdatesListenerDisposer?: () => void
    //#endregion

    //#region Pagination
    contracts = new Pagination(
        async (query) => {
            const requestParams: IExtendedLeaseContractRequest = {
                page_number: query.page,
                page_size: query.pageSize,
                search_term: query.search ?? undefined,
                segment_ids:
                    this.segments.length > 0 ? this.segments : undefined,
            }

            const response =
                await TenantAdminService.postV1AdminLeaseContractList({
                    request: requestParams,
                })

            if (Boolean(query.search)) {
                this.trackSearch()
            }

            const contractsList = response.tenant_contracts ?? []
            const totalCount = response.total_count ?? 0

            const items = contractsList.map(
                (contract: avy_api_pkg_common_TenantContract) => ({
                    id: contract.lease_contract_id ?? 0,
                    agreement_no: contract.agreement_no ?? "",
                    property_owner_name: contract.property_owner_name ?? "",
                    registration_date:
                        contract.registration_date != null
                            ? parseDate(contract.registration_date)
                            : undefined,
                    lease_from:
                        contract.lease_from != null
                            ? parseDate(contract.lease_from)
                            : undefined,
                    lease_to:
                        contract.lease_to != null
                            ? parseDate(contract.lease_to)
                            : undefined,
                    status: contract.status,
                    tenants: contract.name,
                    legal_entity_name: contract.legal_entity_name,
                    property_object_id: contract.property_object_id?.toString(),
                    object_id: contract.object_id,
                    classification: contract.classification,
                }),
            )

            return {
                items,
                rawItems: contractsList,
                count: totalCount,
            }
        },
        { loadingKey: ContractsListStore.LoadingKeys.pageLoad },
    )
    //#endregion

    //#region Constructor
    constructor() {
        makeAutoObservable(this)
    }
    //#endregion

    //#region Filters and actions
    setSegments = (segments: number[]) => {
        this.segments = segments
    }

    @loads(() => ContractsListStore.LoadingKeys.init)
    async init(accessGroupId: number, adminId?: number) {
        this.setAccessGroupId(accessGroupId)
        this.setAdminId(adminId ?? this.adminId)
        await this.contracts.loadInitialPage()
    }

    private setAccessGroupId = (accessGroupId: number) => {
        this.accessGroupId = accessGroupId
    }

    private setAdminId = (adminId: number) => {
        this.adminId = adminId
    }
    //#endregion

    //#region Cleanup
    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }
    //#endregion

    //#region Export
    exportContract = async () => {
        const meta = this.contracts.meta

        const blob =
            await CustomTenantAdminService.postV1AdminLeaseContractList({
                request: {
                    search_term: meta.search ?? undefined,
                    segment_ids: this.segments,
                },
            })

        const url = URL.createObjectURL(blob)
        const filename = `AVY-TMPL-Contract-${new Date().toDateString()}.csv`

        autoDownloadFile(url, filename, "export-contract-link")

        // Keep the url alive for a minute just to be sure.
        setTimeout(() => {
            URL.revokeObjectURL(url)
        }, 60_000)
    }
    //#endregion

    //#region Analytics
    private trackSearch = () => {
        trackModuleEvent("contracts | Search tenant", {
            [MixpanelProperties.SegmentID]: this.segments,
            [MixpanelProperties.UserID]: this.adminId,
        })
    }
    //#endregion
}
